import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MDBIcon } from "mdb-react-ui-kit";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import "./ShowCalendar.css";

const WEEKDAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const LIST_PER_PAGE = 4;

/**
 * Builds a map:  "YYYY-MM-DD" → [ { projectSlug, projectTitle } ]
 */
function buildDateMap(projects) {
  const map = {};
  (projects || []).forEach((p) => {
    (p.representaciones || []).forEach((r) => {
      const d = r.date;
      if (!d) return;
      const dayKey = d.substring(0, 10); // extract YYYY-MM-DD from ISO datetime
      if (!map[dayKey]) map[dayKey] = [];
      map[dayKey].push({ projectSlug: p.slug, projectTitle: p.title || p.name || "Seminario" });
    });
  });
  return map;
}

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function startDayOfMonth(year, month) {
  const d = new Date(year, month, 1).getDay(); // 0=Sun
  return d === 0 ? 6 : d - 1; // convert to Mon=0
}

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Returns flat sorted array of upcoming (today+) representación dates for list view.
 * [ { datetime, dayKey, projectTitle, projectSlug } ]
 */
function buildUpcomingList(projects) {
  const today = todayStr();
  const items = [];
  (projects || []).forEach((p) => {
    (p.representaciones || []).forEach((r) => {
      const d = r.date;
      if (!d) return;
      const dayKey = d.substring(0, 10);
      if (dayKey >= today) {
        items.push({
          datetime: d,
          dayKey,
          projectTitle: p.title || p.name || "Seminario",
          projectSlug: p.slug,
        });
      }
    });
  });
  items.sort((a, b) => a.datetime.localeCompare(b.datetime));
  return items;
}

function formatDateTime(datetimeStr) {
  if (!datetimeStr) return { dayLabel: "", timeLabel: "" };
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(datetimeStr)
    ? datetimeStr + "T00:00:00"
    : datetimeStr;
  const d = new Date(normalized);
  if (isNaN(d.getTime())) return { dayLabel: datetimeStr, timeLabel: "" };
  const dayLabel = d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
  const h = d.getHours();
  const m = d.getMinutes();
  const timeLabel = (h === 0 && m === 0) ? "" : d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  return { dayLabel, timeLabel };
}

export default function ShowCalendar({ fixedMode }) {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [projects, setProjects] = useState([]);
  const [dateMap, setDateMap] = useState({});
  const [tooltipDay, setTooltipDay] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [viewMode, setViewMode] = useState("calendar"); // "calendar" | "list"
  const [listPage, setListPage] = useState(1);
  const tooltipRef = useRef(null);

  // Fetch project data
  useEffect(() => {
    doFetch("MUGIN", "getProyectosLite", { method: "GET" })
      .then((r) => {
        if (!r || !r.ok) return null;
        const contentType = r.headers?.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          throw new Error(`Respuesta no JSON en calendario (${r.status})`);
        }
        return r.json();
      })
      .then((data) => {
        const rows = data && data.DATA ? data.DATA : [];
        setProjects(rows);
        setDateMap(buildDateMap(rows));
      })
      .catch((err) => console.error("ShowCalendar fetch error:", err));
  }, []);

  // Close tooltip on outside click
  useEffect(() => {
    function handler(e) {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target)) {
        setTooltipDay(null);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const today = todayStr();

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const total = daysInMonth(year, month);
  const offset = startDayOfMonth(year, month);
  const monthLabel = new Date(year, month).toLocaleString("es-ES", { month: "long", year: "numeric" });

  const goToProject = (slug) => {
    navigate("/project/" + slug);
  };

  const handleDayClick = (dateStr, shows) => {
    if (!shows || shows.length === 0) return;
    if (shows.length === 1) {
      goToProject(shows[0].projectSlug);
    } else {
      setTooltipDay(dateStr);
    }
  };

  const cells = [];
  // empty offset cells
  for (let i = 0; i < offset; i++) {
    cells.push(<div key={`e${i}`} className="sc-day sc-empty" />);
  }
  // day cells
  for (let d = 1; d <= total; d++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const shows = dateMap[dateStr];
    const hasShow = shows && shows.length > 0;
    const isPast = dateStr <= today; // today counts as past
    const isToday = dateStr === today;

    let cls = "sc-day";
    if (isToday) cls += " sc-today";
    if (hasShow) {
      cls += " sc-has-show";
      cls += isPast ? " sc-past" : " sc-future";
    }

    cells.push(
      <div
        key={d}
        className={cls}
        onClick={() => hasShow && handleDayClick(dateStr, shows)}
        title={hasShow ? shows.map((s) => s.projectTitle).join(", ") : undefined}
      >
        {d}
        {tooltipDay === dateStr && shows && shows.length > 1 && (
          <div className="sc-tooltip" ref={tooltipRef}>
            {shows.map((s, i) => (
              <div key={i} className="sc-tooltip-item" onClick={(e) => { e.stopPropagation(); goToProject(s.projectSlug); }}>
                {s.projectTitle}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── List view ──
  const upcomingList = buildUpcomingList(projects);
  const totalListPages = Math.max(1, Math.ceil(upcomingList.length / LIST_PER_PAGE));
  const safeListPage = Math.min(listPage, totalListPages);
  const listPageItems = upcomingList.slice(
    (safeListPage - 1) * LIST_PER_PAGE,
    safeListPage * LIST_PER_PAGE
  );

  const listBody = (
    <div className="sc-list">
      {listPageItems.length === 0 ? (
        <p className="sc-list-empty">No hay próximas obras programadas.</p>
      ) : (
        <ul className="sc-list-items">
          {listPageItems.map((item, i) => {
            const { dayLabel, timeLabel } = formatDateTime(item.datetime);
            return (
              <li
                key={i}
                className="sc-list-item"
                onClick={() => goToProject(item.projectSlug)}
                title={item.projectTitle}
              >
                <div className="sc-list-date">
                  <MDBIcon fas icon="calendar-day" className="me-1" />
                  {dayLabel}{timeLabel ? ` · ${timeLabel}` : ""}
                </div>
                <div className="sc-list-title">{item.projectTitle}</div>
              </li>
            );
          })}
        </ul>
      )}
      {totalListPages > 1 && (
        <div className="sc-list-pagination">
          <button
            className="sc-list-page-btn"
            disabled={safeListPage <= 1}
            onClick={() => setListPage(safeListPage - 1)}
          >
            <MDBIcon fas icon="chevron-left" />
          </button>
          <span className="sc-list-page-info">{safeListPage} / {totalListPages}</span>
          <button
            className="sc-list-page-btn"
            disabled={safeListPage >= totalListPages}
            onClick={() => setListPage(safeListPage + 1)}
          >
            <MDBIcon fas icon="chevron-right" />
          </button>
        </div>
      )}
    </div>
  );

  const calendarContent = (
    <>
      <div className="sc-header">
        <button onClick={prevMonth}><MDBIcon fas icon="chevron-left" /></button>
        <span className="sc-month-label">{monthLabel}</span>
        <button onClick={nextMonth}><MDBIcon fas icon="chevron-right" /></button>
      </div>
      <div className="sc-weekdays">
        {WEEKDAYS.map((w) => <div key={w}>{w}</div>)}
      </div>
      <div className="sc-days">
        {cells}
      </div>
      <div className="sc-legend">
        <span><span className="sc-legend-dot sc-past-dot" /> Pasadas</span>
        <span><span className="sc-legend-dot sc-future-dot" /> Próximas</span>
      </div>
    </>
  );

  const widgetContent = (
    <>
      {/* Mode toggle */}
      <div className="sc-top-bar">
        <button
          className={`sc-view-btn${viewMode === "calendar" ? " sc-view-btn--active" : ""}`}
          onClick={() => setViewMode("calendar")}
          title="Vista calendario"
        >
          <MDBIcon fas icon="calendar-alt" className="me-1" />
          Calendario
        </button>
        <button
          className={`sc-view-btn${viewMode === "list" ? " sc-view-btn--active" : ""}`}
          onClick={() => { setViewMode("list"); setListPage(1); }}
          title="Próximas obras"
        >
          <MDBIcon fas icon="list" className="me-1" />
          Próximas
        </button>
      </div>
      {viewMode === "calendar" ? calendarContent : listBody}
    </>
  );

  // Fixed mode: locked view without toggle (used in medium-screen side-by-side panel)
  if (fixedMode) {
    return (
      <div className="show-calendar sc-fixed-mode">
        <div className="sc-fixed-label">
          <MDBIcon fas icon={fixedMode === "calendar" ? "calendar-alt" : "list"} className="me-1" />
          {fixedMode === "calendar" ? "Calendario" : "Próximas obras"}
        </div>
        {fixedMode === "calendar" ? calendarContent : listBody}
      </div>
    );
  }

  return (
    <div className="sc-outer">
      {/* Mobile (<768px): toggle button + collapsible widget */}
      <div className="sc-mobile-section">
        <button
          className="sc-mobile-toggle"
          onClick={() => setMobileOpen((o) => !o)}
        >
          <MDBIcon fas icon="calendar-alt" className="me-2" />
          {mobileOpen ? "Ocultar calendario" : "Ver calendario de obras"}
          <MDBIcon fas icon={mobileOpen ? "chevron-up" : "chevron-down"} className="ms-2" />
        </button>
        {mobileOpen && <div className="show-calendar mt-2">{widgetContent}</div>}
      </div>
      {/* Large screens (≥1400px): fixed floating sidebar */}
      <div className="show-calendar sc-float">{widgetContent}</div>
    </div>
  );
}
